/** Materials, recipes and batches. */
const mixing = {
  en: {
    materials: {
      title: "Materials",
      subtitle: "What you mix with. Costs here are what your recipes are priced from.",
      add: "Add a material",
      loadFailed: "Could not load your materials.",
      addFailed: "Could not add that material.",
      updateFailed: "Could not update that stock.",
      retireFailed: "Could not retire that material.",
      types: {
        base: "Base",
        flavor: "Flavour",
        nicotine: "Nicotine",
        additive: "Additive",
        packaging: "Packaging",
      },
      type: "Type",
      name: "Name",
      howMuch: "How much you have ({unit})",
      costPer: "Cost per {unit} ({currency})",
      totalCost: "Total cost paid ({currency})",
      totalCostHint: "What the whole amount above cost you — split across it automatically.",
      costPerComputed: "= {amount} per {unit}",
      strength: "Strength (mg/ml)",
      strengthHint: "Boosters, and any flavour that already carries nicotine.",
      pg: "PG %",
      vg: "VG %",
      recommendedDose: "Maker's dose (% of base)",
      recommendedDoseHint:
        "As printed on the bottle — e.g. \"5% in a 50/50 base\". Offered when this flavour is added to a recipe.",
      sizeMl: "Bottle size (ml)",
      sizeMlHint:
        "Leave blank if this isn't a bottle, or applies to every size — a label, say.",
      everySize: "every size",
      setSize: "Set size",
      saveSize: "Save",
      submit: "Add material",
      perUnit: "{amount} per {unit}",
      low: " · low",
      received: "Received",
      retire: "Retire",
      howMany: "How many {unit}?",
      addToStock: "Add to stock",
      empty:
        "Nothing here yet. Add your bases, flavours and nicotine to start costing your own recipes.",
      stockReceived: "Stock received",
      units: { ml: "ml", pcs: "pcs" },
    },
    recipes: {
      title: "Recipes",
      subtitle:
        "Your own mixes. Pick a ready-mixed base, dose each flavour as a percentage of it — the way the bottle tells you to — and add a nicotine booster only if the mix needs one.",
      new: "New recipe",
      addTitle: "New recipe",
      editTitle: "Edit recipe",
      loadFailed: "Could not load your recipes.",
      saved: "Recipe saved.",
      saveFailed: "Could not save that recipe.",
      deleteConfirm: "Delete this recipe? If it's already been mixed, it's retired instead of deleted, so its batch history is kept.",
      deleteFailed: "Could not delete that recipe.",
      planFailed: "Could not cost that batch.",
      mixFailed: "Could not mix that batch.",
      mixedReady: "Batch mixed — ready on {day}",
      name: "Name",
      fillsProduct: "Fills which product",
      notLinked: "— not linked yet —",
      steep: "Steep (days)",
      steepHint: "Bottles stay off the shelf until this is up.",
      base: "Base",
      pickBase: "— pick a base —",
      baseHint: "Bought ready-mixed. The finished juice keeps this ratio.",
      boosterOptional: "Nicotine booster (optional)",
      noBooster: "— none — the flavour brings its own",
      targetNicotine: "Target mg/ml",
      targetNicotineHint: "How strong the juice should be once the booster is in.",
      noBoosterHint:
        "Leave this off if the flavour already carries the nicotine you want.",
      noBaseChosen: "— no base chosen yet —",
      flavours: "Flavours (dose, % of the base)",
      addFlavour: "+ add a flavour",
      pickFlavour: "— pick a flavour —",
      doseHint:
        "As printed on the flavour, e.g. \"5% in a 50/50 base\". Picking a flavour fills in its maker's dose.",
      save: "Save recipe",
      yours: "Your recipes",
      noneYet: "No recipes yet.",
      summary: "{base} · {steep}d steep",
      summaryBoosted: "{base} · boosted to {nicotine} mg · {steep}d steep",
      mix: "Mix a batch",
      mixing: "Mixing {name}",
      howMuchBase: "How much base are you mixing with?",
      batchSize: "Base (ml)",
      intoBottles: "Into bottles of",
      noBottlesTagged:
        "No packaging is tagged with a bottle size yet — add one in Materials, or this batch won't cost any packaging.",
      finished: "Finishes at",
      finishedDetail: "{volume} ml · {nicotine} mg/ml · {pg}/{vg} PG/VG",
      batchCost: "Batch cost",
      bottles: "{count} × {size} ml bottles",
      each: "{amount} each",
      leftover:
        "{ml} ml left over — its cost is carried by the {count} bottles you fill.",
      readyIn: "Ready to sell in {count} days.",
      notEnough: "Not enough of:",
      shortBy: "{name} — short by {amount} {unit}",
      busy: "Mixing…",
      mixBottles: { one: "Mix {count} bottle", other: "Mix {count} bottles" },
    },
    batches: {
      title: "Batches",
      subtitle: "What you have mixed, and what is still steeping.",
      loadFailed: "Could not load your batches.",
      readyTitle: {
        one: "One batch is ready to bottle",
        other: "{count} batches are ready to bottle",
      },
      readyHint:
        "Their steep is done. Putting them out adds the bottles to your sellable stock.",
      bottlesAdded: "Bottles added to your stock.",
      bottling: "Bottling…",
      putOut: "Put it out for sale",
      steeping: "Steeping",
      leftOfBatch: "{left} left of {total} × {size} ml",
      perBottle: "{amount} a bottle",
      readyNow: "ready now",
      readyTomorrow: "ready tomorrow",
      daysToGo: { one: "{count} day to go", other: "{count} days to go" },
      discarded: "Batch discarded.",
      discardReason: "Discarded by owner",
      discard: "Discard",
      shelveEarly: "Sell before it's ready",
      sellEarly: {
        title: "Sell before it's ready",
        warning:
          "This batch hasn't finished steeping — selling it now can mean a rougher, less blended taste.",
        volumeLabel: "How much are you selling? (ml)",
        volumeHint: "{ml} ml left in this batch",
        bottlesEquivalent: "≈ {count} × {size} ml bottles",
        allRemaining: "All of what's left",
        bottleLabel: "Which bottle is this going into?",
        pickBottle: "— pick a bottle —",
        bottleOption: "{name} · {size} ml · {stock} in stock",
        noBottles:
          "No packaging material has a bottle size set yet — add one in Materials first.",
        includePackaging: "Include packaging cost (bottle & label)",
        costPrice: "Cost price for this",
        priceLabel: "Selling for (total)",
        paymentMethod: "Payment method",
        customer: "Customer",
        tabName: "…or a name for the tab",
        creditNeedsTab: "Scan a customer's card or name the tab to sell on credit.",
        paidUpfront: "Paid up front (optional)",
        staysOnCredit: "Stays on credit",
        submit: "Record the sale",
        submitting: "Selling…",
        tooLittle: "That's less than one bottle",
        tooMuch: "Only {ml} ml is left in this batch",
        needsPrice: "Enter what it's selling for",
        sold: "Sold from the batch.",
        sellFailed: "Could not sell from that batch.",
        noProduct: "This batch has no product linked, so it can't be sold this way.",
      },
      history: "History",
      nothingMixed: "Nothing mixed yet.",
      status: { discarded: "discarded", bottled: "bottled" },
      produced: "{volume} ml → {count} × {size} ml",
      over: " ({ml} ml over)",
      strength: "{mg} mg/ml",
    },
  },

  fr: {
    materials: {
      title: "Matières",
      subtitle:
        "Ce avec quoi vous mélangez. Ces coûts servent à calculer le prix de vos recettes.",
      add: "Ajouter une matière",
      loadFailed: "Impossible de charger vos matières.",
      addFailed: "Impossible d'ajouter cette matière.",
      updateFailed: "Impossible de mettre à jour ce stock.",
      retireFailed: "Impossible de retirer cette matière.",
      types: {
        base: "Base",
        flavor: "Arôme",
        nicotine: "Nicotine",
        additive: "Additif",
        packaging: "Emballage",
      },
      type: "Type",
      name: "Nom",
      howMuch: "Quantité disponible ({unit})",
      costPer: "Coût par {unit} ({currency})",
      totalCost: "Coût total payé ({currency})",
      totalCostHint: "Ce que le tout vous a coûté — réparti automatiquement.",
      costPerComputed: "= {amount} par {unit}",
      strength: "Dosage (mg/ml)",
      strengthHint: "Les boosters, et tout arôme qui contient déjà de la nicotine.",
      pg: "PG %",
      vg: "VG %",
      recommendedDose: "Dosage du fabricant (% de la base)",
      recommendedDoseHint:
        "Comme indiqué sur le flacon — ex. « 5 % dans une base 50/50 ». Proposé à l'ajout de cet arôme dans une recette.",
      sizeMl: "Taille du flacon (ml)",
      sizeMlHint:
        "Laissez vide si ce n'est pas un flacon, ou si ça s'applique à toutes les tailles — une étiquette, par exemple.",
      everySize: "toutes tailles",
      setSize: "Définir la taille",
      saveSize: "Enregistrer",
      submit: "Ajouter la matière",
      perUnit: "{amount} par {unit}",
      low: " · bas",
      received: "Reçu",
      retire: "Retirer",
      howMany: "Combien de {unit} ?",
      addToStock: "Ajouter au stock",
      empty:
        "Rien ici pour l'instant. Ajoutez vos bases, arômes et nicotine pour calculer le coût de vos recettes.",
      stockReceived: "Stock reçu",
      units: { ml: "ml", pcs: "pièces" },
    },
    recipes: {
      title: "Recettes",
      subtitle:
        "Vos propres mélanges. Choisissez une base prête à l'emploi, dosez chaque arôme en pourcentage de cette base — comme indiqué sur le flacon — et n'ajoutez un booster de nicotine que si le mélange en a besoin.",
      new: "Nouvelle recette",
      addTitle: "Nouvelle recette",
      editTitle: "Modifier la recette",
      loadFailed: "Impossible de charger vos recettes.",
      saved: "Recette enregistrée.",
      saveFailed: "Impossible d'enregistrer cette recette.",
      deleteConfirm:
        "Supprimer cette recette ? Si elle a déjà été préparée, elle est retirée plutôt que supprimée, pour garder l'historique des lots.",
      deleteFailed: "Impossible de supprimer cette recette.",
      planFailed: "Impossible de calculer le coût de ce lot.",
      mixFailed: "Impossible de préparer ce lot.",
      mixedReady: "Lot préparé — prêt le {day}",
      name: "Nom",
      fillsProduct: "Produit concerné",
      notLinked: "— pas encore lié —",
      steep: "Maturation (jours)",
      steepHint:
        "Les flacons restent hors rayon jusqu'à la fin de la maturation.",
      base: "Base",
      pickBase: "— choisir une base —",
      baseHint: "Achetée prête à l'emploi. Le mélange final garde ce ratio.",
      boosterOptional: "Booster de nicotine (facultatif)",
      noBooster: "— aucun — l'arôme apporte déjà la sienne",
      targetNicotine: "Dosage cible (mg/ml)",
      targetNicotineHint:
        "La force que le mélange doit atteindre une fois le booster ajouté.",
      noBoosterHint:
        "Laissez vide si l'arôme apporte déjà la nicotine souhaitée.",
      noBaseChosen: "— aucune base choisie —",
      flavours: "Arômes (dosage, % de la base)",
      addFlavour: "+ ajouter un arôme",
      pickFlavour: "— choisir un arôme —",
      doseHint:
        "Comme indiqué sur le flacon, ex. « 5 % dans une base 50/50 ». Choisir un arôme reprend le dosage du fabricant.",
      save: "Enregistrer la recette",
      yours: "Vos recettes",
      noneYet: "Aucune recette pour l'instant.",
      summary: "{base} · {steep} j de maturation",
      summaryBoosted:
        "{base} · boosté à {nicotine} mg · {steep} j de maturation",
      mix: "Préparer un lot",
      mixing: "Préparation : {name}",
      howMuchBase: "Quelle quantité de base préparez-vous ?",
      batchSize: "Base (ml)",
      intoBottles: "En flacons de",
      noBottlesTagged:
        "Aucun emballage n'a encore de taille de flacon associée — ajoutez-en un dans Matières, sinon ce lot n'imputera aucun coût d'emballage.",
      finished: "Résultat final",
      finishedDetail: "{volume} ml · {nicotine} mg/ml · {pg}/{vg} PG/VG",
      batchCost: "Coût du lot",
      bottles: "{count} × flacons de {size} ml",
      each: "{amount} l'unité",
      leftover:
        "{ml} ml restants — leur coût est réparti sur les {count} flacons remplis.",
      readyIn: "Prêt à la vente dans {count} jours.",
      notEnough: "Pas assez de :",
      shortBy: "{name} — il manque {amount} {unit}",
      busy: "Préparation…",
      mixBottles: {
        one: "Préparer {count} flacon",
        other: "Préparer {count} flacons",
      },
    },
    batches: {
      title: "Lots",
      subtitle: "Ce que vous avez préparé, et ce qui est encore en maturation.",
      loadFailed: "Impossible de charger vos lots.",
      readyTitle: {
        one: "Un lot est prêt à être mis en flacons",
        other: "{count} lots sont prêts à être mis en flacons",
      },
      readyHint:
        "Leur maturation est terminée. Les mettre en vente ajoute les flacons à votre stock.",
      bottlesAdded: "Flacons ajoutés à votre stock.",
      bottling: "Mise en flacons…",
      putOut: "Mettre en vente",
      steeping: "En maturation",
      leftOfBatch: "{left} restants sur {total} × {size} ml",
      perBottle: "{amount} le flacon",
      readyNow: "prêt",
      readyTomorrow: "prêt demain",
      daysToGo: { one: "encore {count} jour", other: "encore {count} jours" },
      discarded: "Lot jeté.",
      discardReason: "Jeté par le propriétaire",
      discard: "Jeter",
      shelveEarly: "Vendre avant la fin",
      sellEarly: {
        title: "Vendre avant la fin",
        warning:
          "La maturation de ce lot n'est pas terminée — le vendre maintenant peut donner un goût moins fondu.",
        volumeLabel: "Combien vendez-vous ? (ml)",
        volumeHint: "{ml} ml restants dans ce lot",
        bottlesEquivalent: "≈ {count} × flacons de {size} ml",
        allRemaining: "Tout ce qu'il reste",
        bottleLabel: "Dans quel flacon cela va-t-il ?",
        pickBottle: "— choisir un flacon —",
        bottleOption: "{name} · {size} ml · {stock} en stock",
        noBottles:
          "Aucun emballage n'a encore de taille de flacon définie — ajoutez-en un dans Matières d'abord.",
        includePackaging: "Inclure le coût d'emballage (flacon et étiquette)",
        costPrice: "Prix de revient",
        priceLabel: "Vendu (total)",
        paymentMethod: "Mode de paiement",
        customer: "Client",
        tabName: "…ou un nom pour le compte",
        creditNeedsTab: "Scannez la carte d'un client ou nommez le compte pour vendre à crédit.",
        paidUpfront: "Payé immédiatement (facultatif)",
        staysOnCredit: "Reste à crédit",
        submit: "Enregistrer la vente",
        submitting: "Vente en cours…",
        tooLittle: "C'est moins qu'un flacon",
        tooMuch: "Il ne reste que {ml} ml dans ce lot",
        needsPrice: "Indiquez le prix de vente",
        sold: "Vente enregistrée depuis le lot.",
        sellFailed: "Impossible de vendre depuis ce lot.",
        noProduct: "Ce lot n'est lié à aucun produit, impossible de le vendre ainsi.",
      },
      history: "Historique",
      nothingMixed: "Rien de préparé pour l'instant.",
      status: { discarded: "jeté", bottled: "en flacons" },
      produced: "{volume} ml → {count} × {size} ml",
      over: " ({ml} ml en trop)",
      strength: "{mg} mg/ml",
    },
  },

  ar: {
    materials: {
      title: "المواد",
      subtitle: "ما تمزج به. تُحسب أسعار وصفاتك من هذه التكاليف.",
      add: "إضافة مادة",
      loadFailed: "تعذّر تحميل موادك.",
      addFailed: "تعذّر إضافة هذه المادة.",
      updateFailed: "تعذّر تحديث هذا المخزون.",
      retireFailed: "تعذّر إيقاف هذه المادة.",
      types: {
        base: "قاعدة",
        flavor: "نكهة",
        nicotine: "نيكوتين",
        additive: "مادة مضافة",
        packaging: "تغليف",
      },
      type: "النوع",
      name: "الاسم",
      howMuch: "الكمية المتوفرة ({unit})",
      costPer: "التكلفة لكل {unit} ({currency})",
      totalCost: "التكلفة الإجمالية المدفوعة ({currency})",
      totalCostHint: "ما كلّفك المبلغ كاملاً — يُقسّم تلقائياً على الكمية.",
      costPerComputed: "= {amount} لكل {unit}",
      strength: "التركيز (ملغ/مل)",
      strengthHint: "المعزِّزات، وأي نكهة تحتوي أصلاً على نيكوتين.",
      pg: "PG %",
      vg: "VG %",
      recommendedDose: "جرعة الصانع (% من القاعدة)",
      recommendedDoseHint:
        "كما هو مكتوب على العبوة — مثال: «5% في قاعدة 50/50». تُقترح عند إضافة هذه النكهة إلى وصفة.",
      sizeMl: "حجم القارورة (مل)",
      sizeMlHint:
        "اتركه فارغاً إذا لم تكن هذه قارورة، أو كانت تُستخدم لكل الأحجام — كالملصق مثلاً.",
      everySize: "كل الأحجام",
      setSize: "تحديد الحجم",
      saveSize: "حفظ",
      submit: "إضافة المادة",
      perUnit: "{amount} لكل {unit}",
      low: " · منخفض",
      received: "تم الاستلام",
      retire: "إيقاف",
      howMany: "كم {unit}؟",
      addToStock: "إضافة إلى المخزون",
      empty:
        "لا شيء هنا بعد. أضف القواعد والنكهات والنيكوتين لتبدأ بحساب تكلفة وصفاتك.",
      stockReceived: "استلام مخزون",
      units: { ml: "مل", pcs: "قطعة" },
    },
    recipes: {
      title: "الوصفات",
      subtitle:
        "خلطاتك الخاصة. اختر قاعدة جاهزة، وحدِّد جرعة كل نكهة كنسبة مئوية من هذه القاعدة — كما هو مكتوب على العبوة — وأضف معزِّز نيكوتين فقط إذا احتاجت الخلطة إليه.",
      new: "وصفة جديدة",
      addTitle: "وصفة جديدة",
      editTitle: "تعديل الوصفة",
      loadFailed: "تعذّر تحميل وصفاتك.",
      saved: "تم حفظ الوصفة.",
      saveFailed: "تعذّر حفظ هذه الوصفة.",
      deleteConfirm:
        "حذف هذه الوصفة؟ إذا سبق تحضيرها، سيتم إيقافها بدلاً من حذفها للحفاظ على سجل الدفعات.",
      deleteFailed: "تعذّر حذف هذه الوصفة.",
      planFailed: "تعذّر حساب تكلفة هذه الدفعة.",
      mixFailed: "تعذّر تحضير هذه الدفعة.",
      mixedReady: "تم تحضير الدفعة — جاهزة يوم {day}",
      name: "الاسم",
      fillsProduct: "المنتج المرتبط",
      notLinked: "— غير مرتبطة بعد —",
      steep: "مدة التعتيق (أيام)",
      steepHint: "تبقى القوارير خارج الرفوف حتى انتهاء المدة.",
      base: "القاعدة",
      pickBase: "— اختر قاعدة —",
      baseHint: "تُشترى جاهزة الخلط. تحتفظ الخلطة النهائية بهذه النسبة.",
      boosterOptional: "معزِّز النيكوتين (اختياري)",
      noBooster: "— بدون — النكهة تحمل نيكوتينها الخاص",
      targetNicotine: "التركيز المستهدف (ملغ/مل)",
      targetNicotineHint: "قوة الخلطة المطلوبة بعد إضافة المعزِّز.",
      noBoosterHint: "اتركه فارغاً إذا كانت النكهة تحمل بالفعل النيكوتين المطلوب.",
      noBaseChosen: "— لم تُختر قاعدة بعد —",
      flavours: "النكهات (الجرعة، % من القاعدة)",
      addFlavour: "+ إضافة نكهة",
      pickFlavour: "— اختر نكهة —",
      doseHint:
        "كما هو مكتوب على النكهة، مثال: «5% في قاعدة 50/50». اختيار نكهة يملأ جرعة الصانع تلقائياً.",
      save: "حفظ الوصفة",
      yours: "وصفاتك",
      noneYet: "لا توجد وصفات بعد.",
      summary: "{base} · تعتيق {steep} يوم",
      summaryBoosted: "{base} · مُعزَّزة إلى {nicotine} ملغ · تعتيق {steep} يوم",
      mix: "تحضير دفعة",
      mixing: "تحضير {name}",
      howMuchBase: "ما كمية القاعدة التي تمزجها؟",
      batchSize: "القاعدة (مل)",
      intoBottles: "في قوارير سعة",
      noBottlesTagged:
        "لا يوجد تغليف مرتبط بحجم قارورة بعد — أضف واحداً في المواد، وإلا فلن تُحمَّل هذه الدفعة أي تكلفة تغليف.",
      finished: "النتيجة النهائية",
      finishedDetail: "{volume} مل · {nicotine} ملغ/مل · {pg}/{vg} PG/VG",
      batchCost: "تكلفة الدفعة",
      bottles: "{count} × قوارير {size} مل",
      each: "{amount} للقارورة",
      leftover: "يتبقى {ml} مل — تتحمّل تكلفتها القوارير الـ {count} التي تملؤها.",
      readyIn: "جاهزة للبيع بعد {count} يوم.",
      notEnough: "كمية غير كافية من:",
      shortBy: "{name} — ينقص {amount} {unit}",
      busy: "جارٍ التحضير…",
      mixBottles: {
        one: "تحضير قارورة واحدة",
        two: "تحضير قارورتين",
        few: "تحضير {count} قوارير",
        other: "تحضير {count} قارورة",
      },
    },
    batches: {
      title: "الدفعات",
      subtitle: "ما حضّرته، وما لا يزال قيد التعتيق.",
      loadFailed: "تعذّر تحميل دفعاتك.",
      readyTitle: {
        one: "دفعة واحدة جاهزة للتعبئة",
        two: "دفعتان جاهزتان للتعبئة",
        few: "{count} دفعات جاهزة للتعبئة",
        other: "{count} دفعة جاهزة للتعبئة",
      },
      readyHint:
        "انتهت مدة تعتيقها. عرضها للبيع يضيف القوارير إلى مخزونك القابل للبيع.",
      bottlesAdded: "أُضيفت القوارير إلى مخزونك.",
      bottling: "جارٍ التعبئة…",
      putOut: "عرضها للبيع",
      steeping: "قيد التعتيق",
      leftOfBatch: "تبقى {left} من أصل {total} × {size} مل",
      perBottle: "{amount} للقارورة",
      readyNow: "جاهزة الآن",
      readyTomorrow: "جاهزة غداً",
      daysToGo: {
        one: "بقي يوم واحد",
        two: "بقي يومان",
        few: "بقي {count} أيام",
        other: "بقي {count} يوماً",
      },
      discarded: "تم التخلص من الدفعة.",
      discardReason: "تخلّص منها المالك",
      discard: "تخلّص",
      shelveEarly: "بيعها قبل الجاهزية",
      sellEarly: {
        title: "بيعها قبل الجاهزية",
        warning:
          "لم تنتهِ مدة تعتيق هذه الدفعة بعد — بيعها الآن قد يعطي مذاقاً أقل تناسقاً.",
        volumeLabel: "كم ستبيع؟ (مل)",
        volumeHint: "تبقى {ml} مل في هذه الدفعة",
        bottlesEquivalent: "≈ {count} × قوارير {size} مل",
        allRemaining: "كل ما تبقى",
        bottleLabel: "في أي قارورة سيوضع هذا؟",
        pickBottle: "— اختر قارورة —",
        bottleOption: "{name} · {size} مل · {stock} في المخزون",
        noBottles: "لا يوجد تغليف محدد له حجم قارورة بعد — أضف واحداً في المواد أولاً.",
        includePackaging: "احتساب تكلفة التغليف (القارورة والملصق)",
        costPrice: "سعر التكلفة لهذه الكمية",
        priceLabel: "سعر البيع (الإجمالي)",
        paymentMethod: "طريقة الدفع",
        customer: "الزبون",
        tabName: "…أو اسم للحساب",
        creditNeedsTab: "امسح بطاقة الزبون أو سمِّ الحساب للبيع بالدين.",
        paidUpfront: "المدفوع فوراً (اختياري)",
        staysOnCredit: "يبقى ديناً",
        submit: "تسجيل عملية البيع",
        submitting: "جارٍ البيع…",
        tooLittle: "هذا أقل من قارورة واحدة",
        tooMuch: "تبقى {ml} مل فقط في هذه الدفعة",
        needsPrice: "أدخل سعر البيع",
        sold: "تم البيع من الدفعة.",
        sellFailed: "تعذّر البيع من هذه الدفعة.",
        noProduct: "هذه الدفعة غير مرتبطة بمنتج، لا يمكن بيعها بهذه الطريقة.",
      },
      history: "السجل",
      nothingMixed: "لم يُحضَّر شيء بعد.",
      status: { discarded: "مُتخلَّص منها", bottled: "معبّأة" },
      produced: "{volume} مل → {count} × {size} مل",
      over: " ({ml} مل زائدة)",
      strength: "{mg} ملغ/مل",
    },
  },
};

export default mixing;
